/**
 * Grade-specific mascot for elementary students.
 *
 * Each elementary grade (el1-el6) gets a distinct animal mascot that
 * renders as a large emoji with a gentle idle animation. Older tracks
 * (ms*, *u) return null — we don't want to bro-down to middle-schoolers.
 *
 * Pure CSS + unicode emoji; no Lottie, no SVG asset pipeline.
 */
import { memo } from 'react';

export interface MascotInfo {
  emoji: string;
  name: string;
  /** Tailwind-compatible hex; used for accent glows. */
  color: string;
}

export const MASCOTS: Record<string, MascotInfo> = {
  el1: { emoji: '🦊', name: 'שועלון', color: '#fb923c' },
  el2: { emoji: '🐻', name: 'דובי', color: '#a78bfa' },
  el3: { emoji: '🦉', name: 'ינשוף', color: '#fbbf24' },
  el4: { emoji: '🐯', name: 'נמרון', color: '#fb7185' },
  el5: { emoji: '🦁', name: 'אריון', color: '#facc15' },
  el6: { emoji: '🐲', name: 'דרקון', color: '#34d399' },
};

interface Props {
  track: string | undefined | null;
  /** sm = 28px, md = 48px, lg = 96px */
  size?: 'sm' | 'md' | 'lg';
  /** Default true. Set false on small screens / when motion is undesirable. */
  animate?: boolean;
  className?: string;
}

const SIZE_PX: Record<NonNullable<Props['size']>, number> = {
  sm: 28,
  md: 48,
  lg: 96,
};

function GradeMascotInner({ track, size = 'md', animate = true, className = '' }: Props) {
  const mascot = track ? MASCOTS[track] : undefined;
  if (!mascot) return null;
  const px = SIZE_PX[size];
  return (
    <span
      className={`inline-block leading-none select-none ${animate ? 'mascot-bob' : ''} ${className}`}
      style={{
        fontSize: `${px}px`,
        filter: `drop-shadow(0 2px 6px ${mascot.color}55)`,
      }}
      role="img"
      aria-label={mascot.name}
      title={mascot.name}
    >
      {mascot.emoji}
    </span>
  );
}

export const GradeMascot = memo(GradeMascotInner);
export default GradeMascot;

export function getMascot(track: string | undefined | null): MascotInfo | undefined {
  if (!track) return undefined;
  return MASCOTS[track];
}
